import React, { useRef, useState, useEffect, createContext, useContext, useCallback } from 'react';
import useScrolledShadow from 'hooks/useScrolledShadow';

// Contexte pour partager l'état du scroll horizontal, du drag et de la ligne survolée
const ScrollContext = createContext({ hasHorizontalScroll: false, hasMoved: false, hoveredRowId: null, setHoveredRowId: () => {} });

export const useScrollContext = () => useContext(ScrollContext);

/**
 * Wrapper pour permettre le drag horizontal avec la souris sur mobile/tablette
 * @param {React.ReactNode} children - Contenu de la table à wrapper
 * @param {string} className - Classes CSS additionnelles
 */
const ScrollableTableWrapper = ({ children, className = '' }) => {
  const scrollContainerRef = useRef(null);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [hasMoved] = useState(false);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const { ref: watchScroll, isScrolled } = useScrolledShadow();

  const setScrollContainer = useCallback((node) => {
    scrollContainerRef.current = node;
    watchScroll(node);
  }, [watchScroll]);

  // Détecter si le scroll horizontal est nécessaire
  useEffect(() => {
    const checkScroll = () => {
    const container = scrollContainerRef.current;
      if (container) {
        const hasScroll = container.scrollWidth > container.clientWidth;
        setHasHorizontalScroll(hasScroll);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    
    // Observer les changements de taille du contenu
    const resizeObserver = new ResizeObserver(checkScroll);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkScroll);
      resizeObserver.disconnect();
    };
  }, [children]);

  return (
    <ScrollContext.Provider value={{ hasHorizontalScroll, hasMoved, hoveredRowId, setHoveredRowId }}>
    <div
      ref={setScrollContainer}
      // On the header cells: a sticky <thead> paints no box-shadow in every
      // browser, a cell does. The head is lifted above the sticky first-column
      // cells (z-10 too): on a tie the shadowed head lost to the rows scrolling
      // under it and showed a body cell in its corner.
      className={`overflow-auto h-full w-full thin-scrollbar [&>table>thead]:z-20 [&>table>thead_th]:transition-shadow [&>table>thead_th]:duration-150 ${isScrolled ? '[&>table>thead_th]:shadow-[0_4px_6px_-4px_rgba(17,24,39,0.12)]' : ''} ${className}`}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {children}
    </div>
    </ScrollContext.Provider>
  );
};

export default ScrollableTableWrapper;
